import Image from 'next/image'
import { useRouter } from 'next/router'
import { useBogo } from '@mc/bogo-utils'
import { Conditional, ShowIf } from '@mc/conditional-rendering'
import { useTranslation } from '@mc/i18n'
import { cloudflareLoader } from '@mc/image-cloudflare'
import { paths } from '@mc/paths'
import { usePromotion } from '@mc/promo-utils'
import { PromoGiftHero } from '@mc/promos'
import CTA from 'components/CTA'
import { usePen7647GiftHeroCards } from 'experiments/pen-7647_v1_gift_hero_card'
import { STATIC_IMAGE_BASE } from 'libs/constants'
import classes from './Hero.module.scss'

const STEP_KEYS = ['one', 'two', 'three'] as const

const HERO_BACK_CARD_SLUG = 'generic'
const HERO_MIDDLE_CARD_SLUG = 'thanks-dad'
const HERO_TOP_CARD_SLUG = 'happy-birthday'

const CARD_WIDTH = 1040
const CARD_HEIGHT = 582

const DefaultHero = () => {
  const { t } = useTranslation('@mc/next')
  return (
    <>
      <h1 className={`mc-text-b1 mc-mb-3 ${classes.title}`}>
        {t('gift.hero.title')}
      </h1>
      <p
        className={`mc-text-large mc-opacity--muted mc-mb-6 ${classes.subtitle}`}
      >
        {t('gift.hero.subtitle')}
      </p>
    </>
  )
}

export const Hero = () => {
  const { t } = useTranslation('@mc/next')
  const { query } = useRouter()

  const { isPromotionOn, variables } = usePromotion({ isGift: true })
  const { isBogoOfferEligible } = useBogo()

  const promoHeroCardSlug = isPromotionOn && variables.gift_page?.hero_image

  const cards = usePen7647GiftHeroCards({
    control: {
      top: HERO_TOP_CARD_SLUG,
      middle: HERO_MIDDLE_CARD_SLUG,
      back: HERO_BACK_CARD_SLUG,
    },
    isPromoHeroOverride: !!promoHeroCardSlug,
  })

  const heroTopCardSlug = promoHeroCardSlug || cards.top
  const heroMiddleCardSlug = promoHeroCardSlug
    ? HERO_TOP_CARD_SLUG
    : cards.middle

  return (
    <section className='position-relative mc-overflow--hidden mc-pt-5 mc-pb-9 mc-py-md-10'>
      <div className={`container ${classes.inner}`}>
        <div className={classes.topRow}>
          <div className={classes.textColumn}>
            <Image
              src={`${STATIC_IMAGE_BASE}/CM_instructors_hero_2x.png`}
              alt={t('gift.hero.avatarsAlt')}
              className={classes.avatars}
              width={183}
              height={38}
              priority
            />

            <Conditional>
              <ShowIf condition={isPromotionOn || isBogoOfferEligible}>
                <PromoGiftHero
                  titleClassName={`mc-text-b1 mc-mb-3 ${classes.title}`}
                />
              </ShowIf>
              <ShowIf isDefault>
                <DefaultHero />
              </ShowIf>
            </Conditional>

            <CTA
              className={`${classes.cta} mc-mb-4`}
              href={paths.checkoutGift(query)}
              trackProps={{
                action: 'aap-gift-landing',
                content_type: 'product',
                location: 'gifting-landing-hero',
              }}
            >
              {t('gift.hero.cta')}
            </CTA>
          </div>
          <div className={classes.cardsWrap}>
            <div className={classes.confetti} aria-hidden>
              <Image
                src={`${STATIC_IMAGE_BASE}/confetti.png`}
                alt=''
                fill
                priority
                sizes='(min-width: 768px) 45vw, 100vw'
                quality={50}
              />
            </div>
            <div className={classes.cardStack}>
              <Image
                loader={cloudflareLoader}
                src={`${STATIC_IMAGE_BASE}/gift-template-${cards.back}.png`}
                alt=''
                aria-hidden
                width={CARD_WIDTH}
                height={CARD_HEIGHT}
                sizes='(min-width: 768px) 400px, 280px'
                quality={80}
                loading='lazy'
                className={`${classes.card} ${classes.cardBack}`}
              />
              <Image
                loader={cloudflareLoader}
                src={`${STATIC_IMAGE_BASE}/gift-template-${heroMiddleCardSlug}.png`}
                alt=''
                aria-hidden
                width={CARD_WIDTH}
                height={CARD_HEIGHT}
                sizes='(min-width: 768px) 400px, 280px'
                quality={80}
                loading='lazy'
                className={`${classes.card} ${classes.cardMiddle}`}
              />
              <Image
                loader={cloudflareLoader}
                src={`${STATIC_IMAGE_BASE}/gift-template-${heroTopCardSlug}.png`}
                alt={t('gift.hero.cardsAlt')}
                width={CARD_WIDTH}
                height={CARD_HEIGHT}
                sizes='(min-width: 768px) 400px, 280px'
                quality={80}
                priority
                className={`${classes.card} ${classes.cardTop}`}
              />
            </div>
          </div>
        </div>

        <ol className={`${classes.steps} mc-mt-10 mc-mt-md-12 mc-pl-0`}>
          {STEP_KEYS.map((key, i) => (
            <li key={key} className={classes.step}>
              <h3 className={`mc-text-h5 ${classes.stepHeading}`}>
                <span className={classes.stepNumber}>{i + 1}</span>
                <span>{t(`gift.hero.steps.${key}.title`)}</span>
              </h3>
              <p
                className={`mc-text-base mc-opacity--muted mc-mt-2 ${classes.stepDescription}`}
              >
                {t(`gift.hero.steps.${key}.description`)}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}
